import React, { useState } from 'react';
import './Highlights_dec.css';

// Type definition for component props
interface HighlightsProps {
  onNavigateHome: () => void;
}

// Type definition for a highlight post
interface HighlightPost {
  id: string;
  title: string;
  imageUrl: string;
  description: string;
  postedAt: Date;
}

// Type definition for modal props
interface ModalProps {
  post: HighlightPost | null;
  isOpen: boolean;
  onClose: () => void;
}

const Highlights: React.FC<HighlightsProps> = ({ onNavigateHome }) => {
  const [selectedPost, setSelectedPost] = useState<HighlightPost | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Sample data for demonstration - in a real app, this would come from an API
  const highlightPosts: HighlightPost[] = [
    {
      id: "9",
      title: "Movie Night", 
      imageUrl: "/Movie Night.jpg",
      description: "Join the Africa Development Group (ADG) this Wednesday, October 28th, starting at 8:00 PM for a special Open Mic Night dedicated to the power of African cinema. We’ll be showcasing one of the most impactful African movies of all time—a film that perfectly captures the rich culture, complex history, and brilliant storytelling emerging from the continent's film industry. Come ready to celebrate and appreciate African media with us.",
      postedAt: new Date('2025-10-29')
    },
    {
      id: "8",
      title: "Open Mic Night", 
      imageUrl: "/Open Mic Night.jpg",
      description: "Join us for our Open Mic Night on Wednesday, October 15th starting at 8:00 PM! The Africa Development Group (ADG) presents an evening to share your talents and enjoy the creativity of others. The event will be held in Hamilton 516, and best of all, entry is absolutely free!.",
      postedAt: new Date('2025-10-09')
    },
    {
      id: "7",
      title: "Identity Politics in the Diaspora", 
      imageUrl: "/ID Politics2.jpg",
      description: "Join us for our 'Identity Politics in the Diaspora' event on October 1st at 8:00 PM. The Africa Development Group (ADG) presents a night of thought-provoking conversation and community. We'll be discussing hot topics while enjoying hot food! This is a great opportunity to engage with members of ADG and the wider diasporic community. We look forward to a night of lively discussion and connecting with you all. Location to be announced (TBD).",
      postedAt: new Date('2025-09-28')
    },
    {
      id: "6",
      title: "Picnic Night", 
      imageUrl: "/ADG Picnic Night.jpg",
      description: "Join us for our 'Picnic @ Night' event on September 17th at 8:00 PM on Butler Lawns. The Africa Development Group (ADG) invites you to come and get to know members from ADG and the wider diasporic community. We'll be sharing an evening of 'Moonlight, Snacks, Laughter, and Chill Vibes' and look forward to building connections with everyone who attends.",
      postedAt: new Date('2025-09-15')
    },
    {
      id: "5",
      title: "ISSO Cultural Fair", 
      imageUrl: "/ISSO Cultural Fair.jpg",
      description: "Get ready for the ISSO Cultural Fair! Join the International Student Scholars Office (ISSO) as they collaborate with various Columbia University International Student Groups for a vibrant celebration of cultures from around the world. The event will feature a lively Cultural Festival, an Inflatable Adventure Course, and Henna Tattoos. Come learn directly from your fellow students! The fair will be held on Monday, September 22, from 12:00 PM to 4:00 PM on South Lawn, 500 W. 116 St. We can't wait to see you there!",
      postedAt: new Date('2025-09-09')
    },
    {
      id: "4", 
      title: "The politics of Food Aid",
      imageUrl: "/Food Aid.jpg",
      description: "Join us for an insightful discussion on 'The Politics of Food Aid' and its crucial role in Africa. This event will explore the complexities and impacts of international aid on the continent, fostering a deeper understanding of the subject. Mark your calendars for Wednesday, February 26, from 8:30 to 9:30 PM, and meet us in Hamilton 702 to be part of this vital conversation. We hope to see you there!",
      postedAt: new Date('2025-02-26')
    },
    {
      id: "3",
      title: "Is being African Inherently political?",
      imageUrl: "/African Address Identity.jpg",
      description: "Explore the intersection of identity and politics in Africa at 'Is Being African Inherently Political?' Join us for a thought-provoking discussion on how Africans address their identity. This event will delve into the diverse and complex factors that shape African identity today. Come and be part of the conversation on Wednesday, March 12, from 8:30 to 9:30 PM in Hamilton 702. We look forward to seeing you there",
      postedAt: new Date('2024-12-03')
    },
    {
      id: "2",
      title: "Research Symposium",
      imageUrl: "/Research symposium.jpg", 
      description: "You are cordially invited to the African Development Group Research Symposium. Join us for an evening of scholarly insight and discussion. This year, we are honored to feature a presentation by Ousseina D. Alidou, Ph.D., who will share her extensive work on the sociopolitical gender dynamics within African Muslim societies. The symposium will take place on November 20, 2024, from 8:00 PM to 9:00 PM. We hope to see you there!",
      postedAt: new Date('2024-11-20')
    },
    {
      id: "1",
      title: "La Noire De...", 
      imageUrl: "/La Noire.jpg",
      description: "You're invited to a special screening of 'La Noire de...'! Join us for a powerful cinematic experience. This iconic film explores themes of colonialism, identity, and displacement. We look forward to seeing you there on Wednesday, October 23, from 8:30 to 9:30 PM in Pupin 428.",
      postedAt: new Date('2024-10-23')
    },
  ];

  // Function to calculate time passed since post
  const getTimeAgo = (date: Date): string => {
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const diffWeeks = Math.floor(diffDays / 7);
    const diffMonths = Math.floor(diffDays / 30);

    if (diffDays < 1) {
      return 'Today';
    } else if (diffDays === 1) {
      return '1 day ago';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else if (diffWeeks === 1) {
      return '1 week ago';
    } else if (diffWeeks < 4) {
      return `${diffWeeks} weeks ago`;
    } else if (diffMonths === 1) {
      return '1 month ago';
    } else {
      return `${diffMonths} months ago`;
    }
  };

  // Navigation function to go back to Home
  const navigateToHome = () => {
    onNavigateHome();
  };


  // Modal functions
  const openModal = (post: HighlightPost) => {
    setSelectedPost(post);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setSelectedPost(null);
    setIsModalOpen(false);
  };

  // Modal component
  const Modal: React.FC<ModalProps> = ({ post, isOpen, onClose }) => {
    if (!isOpen || !post) return null;

    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-container" onClick={(e) => e.stopPropagation()}>
          <button className="modal-close" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          
          <div className="modal-body">
            <div className="modal-image-container">
              <img 
                src={post.imageUrl} 
                alt={post.title}
                className="modal-image"
              />
            </div>
            
            <div className="modal-content">
              <h2 className="modal-title">{post.title}</h2>
              <p className="modal-date">{getTimeAgo(post.postedAt)}</p>
              <p className="modal-description">{post.description}</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Component for individual highlight post
  const HighlightCard: React.FC<{ post: HighlightPost }> = ({ post }) => (
    <div className="highlight-card" onClick={() => openModal(post)}>
      <div className="image-container">
        <img src={post.imageUrl} alt={post.title} className="highlight-image" />
        <div className="time-badge">
          {getTimeAgo(post.postedAt)}
        </div>
      </div>
      <div className="content-container">
        <h3 className="highlight-title">{post.title}</h3>
        <p className="highlight-description">{post.description}</p>
        <button className="read-more-btn">Read More</button>
      </div>
    </div>
  );

  return (
    <div className="highlights-page">
      {/* Header with navigation */}
      <div className="highlights-nav">
        <button onClick={navigateToHome} className="back-to-home">
          <img 
            src="https://cdn-icons-png.flaticon.com/128/15967/15967383.png" 
            alt="Africa Continent" 
            className="nav-africa-icon"
          />
          <span>ADG</span>
        </button>
        
        <nav className="highlights-header-nav">
          <button onClick={navigateToHome} className="nav-item">Home</button>
          <button className="nav-item active">Highlights</button>
        </nav>
      </div>

      <div className="highlights-header">
        <h1 className="page-title">Session Highlights</h1>
        <p className="page-subtitle">
          Stay updated with our upcoming sessions and educational discussions about African development, 
          governance, and community empowerment.
        </p>
      </div>

      <div className="highlights-container">
        {highlightPosts.map((post) => (
          <HighlightCard key={post.id} post={post} />
        ))}
      </div>

      <Modal 
        post={selectedPost}
        isOpen={isModalOpen}
        onClose={closeModal}
      />
    </div>
  );
};

export default Highlights;